// Clientes del Portal Admin SureKeep — la mirada de CRÉDITO del padrón. UNA sola llamada arma la
// tabla: `GET /backoffice/credit-customers`, el BFF de `loan-credit-business`, que cruza padrón,
// cartera y segmentos y pagina con cursor opaco.
//
// El front acumula páginas con `useInfiniteQuery` y filtra, ordena y pagina sobre lo acumulado.
// Mientras queden páginas, la tabla NO tiene el padrón completo y la pantalla lo declara: los
// conteos dicen sobre qué cuentan y el filtro dice que mira lo cargado (ESTANDAR-TABLAS §5.4).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { ScreenFrame } from '@/shared/patterns/ScreenFrame';
import { CUSTOMERS_NOUN, loadedFiltersNote } from '@/shared/patterns/LoadedScope';
import { userMessageOf } from '@/api/errors';
import { useCustomerCredit } from '@/domains/sales/portal/customers/api/useCustomerCredit';
import { useCreditCustomers } from './api/useCreditCustomers';
import { listStateOf } from './lib/creditCustomersErrors';
import { CreditCustomersFiltersPanel } from './components/CreditCustomersFiltersPanel';
import { CreditCustomersTable } from './components/CreditCustomersTable';
import { useCreditCustomersTable } from './components/creditCustomersTableModel';
import { CreditCustomerDetailPanel, type DetailTab } from './components/CreditCustomerDetailPanel';
import {
    anySegmentsRead,
    countActiveFilters,
    DEFAULT_SORT,
    EMPTY_FILTERS,
    segmentOptionsOf,
    type CreditBucket,
    type CreditCustomerRow,
    type CreditCustomersFilters,
    type SortState,
} from './lib/creditCustomersFilters';

const NO_ROWS: CreditCustomerRow[] = [];

/** El error de una query de TanStack llega tipado laxo; la UI necesita un `Error` o nada. */
function asError(error: unknown): Error | null {
    if (!error) return null;
    if (error instanceof Error) return error;
    return new Error(typeof error === 'string' ? error : 'Error desconocido');
}

export function CreditCustomersPage({
    customerId,
    tab,
}: {
    /** Deep-link `?cliente=` — el cliente abierto en la ficha. */
    customerId: string | undefined;
    tab: DetailTab;
}) {
    const navigate = useNavigate();
    const [filters, setFilters] = useState<CreditCustomersFilters>(EMPTY_FILTERS);
    const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);

    const list = useCreditCustomers();
    // El fallo solo vacía la pantalla cuando NO hay nada cargado. Con páginas en mano, tirarlas por
    // un tropiezo de red sería lo contrario de acumular: el pie dice qué pasó y las filas se quedan.
    const listError = list.data ? null : asError(list.error);
    const listState = listStateOf({ isPending: list.isPending, error: listError });

    // useMemo OBLIGATORIO: `useReactTable` compara `data` por referencia. Sin memo el `flatMap`
    // devuelve un array nuevo cada render → re-render infinito y la pantalla se congela.
    const rows = useMemo(
        () => list.data?.pages.flatMap((page) => page.items) ?? NO_ROWS,
        [list.data],
    );

    const { table, pageRows, filteredCount } = useCreditCustomersTable({
        rows,
        filters,
        sort,
        onSortChange: setSort,
    });

    const segmentOptions = useMemo(() => segmentOptionsOf(rows), [rows]);
    const segmentsRead = useMemo(() => rows.length === 0 || anySegmentsRead(rows), [rows]);

    const selectedRow = rows.find((row) => row.retailCustomerId === customerId) ?? null;
    const credit = useCustomerCredit(customerId ?? null);

    const selectCustomer = (nextId: string | null, nextTab?: DetailTab) => {
        void navigate({
            to: '/credit/customers',
            search: { cliente: nextId ?? undefined, tab: nextTab ?? tab },
            replace: true,
        });
    };

    /** El último id que la tabla llegó a mostrar seleccionado. */
    const shownSelectionRef = useRef<string | null>(null);

    /**
     * Sin `?cliente=` abre la primera fila, y si la abierta cae fuera del filtro salta a la primera
     * visible. Un `?cliente=` recién llegado por la URL se respeta aunque no esté en la lista.
     */
    useEffect(() => {
        if (!list.isSuccess) return;
        if (!customerId) {
            if (pageRows.length > 0) selectCustomer(pageRows[0].retailCustomerId);
            return;
        }
        if (pageRows.some((row) => row.retailCustomerId === customerId)) {
            shownSelectionRef.current = customerId;
            return;
        }
        if (shownSelectionRef.current !== customerId) return;
        shownSelectionRef.current = null;
        selectCustomer(pageRows.length > 0 ? pageRows[0].retailCustomerId : null);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- selectCustomer se re-crea en cada render; depender de él dispararía el efecto en bucle
    }, [customerId, pageRows, list.isSuccess]);

    return (
        <ScreenFrame>
            <MasterDetailExplorer
                storageKey="credit-customers"
                filtersCollapsed={filtersCollapsed}
                filters={
                    <CreditCustomersFiltersPanel
                        filters={filters}
                        onChange={setFilters}
                        listState={listState}
                        collapsed={filtersCollapsed}
                        onToggleCollapsed={() => {
                            setFiltersCollapsed((value) => !value);
                        }}
                        segmentOptions={segmentOptions}
                        segmentsRead={segmentsRead}
                        loadedNote={loadedFiltersNote(
                            { loaded: rows.length, hasMore: list.hasNextPage },
                            CUSTOMERS_NOUN,
                        )}
                    />
                }
                list={
                    <CreditCustomersTable
                        table={table}
                        pageRows={pageRows}
                        allRows={rows}
                        filteredCount={filteredCount}
                        sort={sort}
                        onSortChange={setSort}
                        bucket={filters.bucket}
                        onBucketChange={(next: CreditBucket) => {
                            setFilters({ ...filters, bucket: next });
                        }}
                        selectedId={customerId ?? null}
                        onSelect={(id) => {
                            selectCustomer(id);
                        }}
                        isLoading={listState === 'loading'}
                        hasFilters={countActiveFilters(filters) > 0}
                        error={listError}
                        onRetry={() => {
                            void list.refetch();
                        }}
                        hasNextPage={list.hasNextPage}
                        isFetchingNextPage={list.isFetchingNextPage}
                        onLoadMore={() => {
                            void list.fetchNextPage();
                        }}
                        nextPageError={list.isFetchNextPageError ? userMessageOf(list.error) : null}
                    />
                }
                detail={
                    <CreditCustomerDetailPanel
                        row={selectedRow}
                        credit={credit.detail}
                        onRetryCredit={credit.refetch}
                        tab={tab}
                        onTabChange={(nextTab) => {
                            selectCustomer(customerId ?? null, nextTab);
                        }}
                    />
                }
            />
        </ScreenFrame>
    );
}
